import { Action } from "domain/aangifte";
import { rubriekenFp, rubriekFp } from "domain/rubriek";
import { array, option, string } from "fp-ts";
import { flow, pipe } from "fp-ts/lib/function";
import { RubriekReducer } from "../rubriekenReducer";

const exclusive = (codes: string[], action: Action) => (rubrieken: RubriekReducer) => {
  switch (action.type) {
    case "UPDATE_RUBRIEK_VALUE":
      return pipe(
        codes,
        array.findFirst((s) => s === action.code),
        option.map((code) => array.difference(string.Eq)([code])(codes)),
        option.chain(
          array.reduce(option.of(rubrieken), (acc, codeToDelete) => {
            return pipe(acc, option.chain(rubriekenFp.modifyAt(codeToDelete, rubriekFp.clearValue)));
          })
        ),
        option.getOrElse(() => rubrieken)
      );
    default:
      return rubrieken;
  }
};

const disableRubriek = rubriekFp.modifyAt("disabled", true);
const enableRubriek = rubriekFp.modifyAt("disabled", false);

const dependsOn = (parent: string) => (child: string) => (rubrieken: RubriekReducer): RubriekReducer => {
  return pipe(
    rubrieken,
    rubriekenFp.lookupValue(parent),
    option.fold(
      () => pipe(rubrieken, rubriekenFp.modifyAt(child, flow(disableRubriek, rubriekFp.clearValue))),
      () => pipe(rubrieken, rubriekenFp.modifyAt(child, enableRubriek))
    ),
    option.getOrElse(() => rubrieken)
  );
};

const dontClear = (code: string, action: Action) => (rubrieken: RubriekReducer): RubriekReducer => {
  if (action.type !== "UPDATE_RUBRIEK_VALUE" || action.code !== code || action.value === true) return rubrieken;

  return pipe(
    rubrieken,
    rubriekenFp.modifyAt(code, rubriekFp.modifyAt("value", true)),
    option.getOrElse(() => rubrieken)
  );
};

export const runBurgerlijkeStaatRules = (action: Action) => (rubrieken: RubriekReducer): RubriekReducer => {
  return pipe(
    rubrieken,
    exclusive(["1001", "1002", "1010", "1020", "1021", "1022"], action),
    dontClear("1001", action),
    dontClear("1002", action),
    dontClear("1010", action),
    dontClear("1020", action),
    dontClear("1021", action),
    dontClear("1022", action),
    pipe("1003", dependsOn("1002")),
    pipe("1004", dependsOn("1003")),
    pipe("1011", dependsOn("1010")),
    pipe("1018", dependsOn("1002")),
    pipe("1019", dependsOn("1018")),
    pipe("1023", dependsOn("1022")),
    pipe("1024", dependsOn("1022")),
    exclusive(["1023", "1024"], action)
  );
};
